/**
 * Blocks rail "customized in N accounts" popover (NA-295). Lists the
 * currently-assigned accounts with an English override on a block, each
 * with a distinct "Open in this account →" action into that account's
 * content context. Follows BlockTypeChooser's portal + anchored-position +
 * outside-click/Esc dismiss pattern.
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@course-authoring/shared-types';

const VISIBLE_ROWS = 6;
const ROW_HEIGHT = 38;

export interface BlockCustomizationAccount {
  id: string;
  name: string;
}

interface BlockCustomizationPopoverProps {
  accounts: BlockCustomizationAccount[];
  expectedCount: number;
  position: { x: number; y: number };
  triggerElement: HTMLElement;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onClose: () => void;
  onSelectAccount: (account: BlockCustomizationAccount) => void;
}

export function BlockCustomizationPopover({
  accounts,
  expectedCount,
  position,
  triggerElement,
  isLoading,
  isError,
  onRetry,
  onClose,
  onSelectAccount,
}: BlockCustomizationPopoverProps) {
  const { tokens } = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        ref.current
        && !ref.current.contains(target)
        && !triggerElement.contains(target)
      ) {
        onClose();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, triggerElement]);

  useLayoutEffect(() => {
    const clampToViewport = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const margin = 8;
      const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
      const maxTop = Math.max(margin, window.innerHeight - rect.height - margin);
      const nextPosition = {
        x: Math.max(margin, Math.min(position.x, maxLeft)),
        y: Math.max(margin, Math.min(position.y, maxTop)),
      };
      setAdjustedPosition((current) => (
        current.x === nextPosition.x && current.y === nextPosition.y
          ? current
          : nextPosition
      ));
    };

    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [accounts.length, expectedCount, isError, isLoading, position.x, position.y]);

  const headerText = `Customized in ${expectedCount} account${expectedCount === 1 ? '' : 's'}`;
  const hasMissingAccountDetails = !isLoading && !isError && accounts.length < expectedCount;

  const styles: Record<string, CSSProperties> = {
    container: {
      position: 'fixed',
      left: adjustedPosition.x,
      top: adjustedPosition.y,
      display: 'flex',
      flexDirection: 'column',
      width: '270px',
      maxHeight: `${ROW_HEIGHT * VISIBLE_ROWS + 40}px`,
      backgroundColor: tokens.backgrounds.surface,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
      zIndex: 100,
      overflow: 'hidden',
    },
    // Same muted section-label treatment as the canvas's "TEXT CALLOUT · B298"
    // type label (ContentBlockWrapper's typeLabel style).
    header: {
      flex: '0 0 auto',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: tokens.text.subtle,
    },
    list: {
      flex: '1 1 auto',
      overflowY: 'auto',
    },
    message: {
      padding: `${tokens.spacing.md}`,
      borderTop: `1px solid ${tokens.borders.default}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.muted,
    },
    retryButton: {
      marginTop: tokens.spacing.sm,
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.link,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderTop: `1px solid ${tokens.borders.default}`,
    },
    accountName: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.primary,
    },
    openLink: {
      flex: '0 0 auto',
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      color: tokens.text.link,
      whiteSpace: 'nowrap',
    },
  };

  return createPortal(
    <div ref={ref} style={styles.container} role="dialog" aria-label={headerText}>
      <div style={styles.header}>{headerText}</div>
      {isLoading ? (
        <div role="status" style={styles.message}>Loading account details…</div>
      ) : isError ? (
        <div role="alert" style={styles.message}>
          <div>Couldn’t load account details.</div>
          <button type="button" style={styles.retryButton} onClick={onRetry}>Retry</button>
        </div>
      ) : (
        <>
          {hasMissingAccountDetails ? (
            <div role="status" style={styles.message}>Some account details are unavailable.</div>
          ) : null}
          <div style={styles.list} role="list">
            {accounts.map((account) => (
              <div key={account.id} role="listitem" style={styles.row}>
                <span style={styles.accountName}>{account.name}</span>
                <button
                  type="button"
                  style={styles.openLink}
                  aria-label={`Open in this account: ${account.name}`}
                  onClick={() => onSelectAccount(account)}
                >
                  Open in this account →
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
